import { useEffect, useMemo, useRef } from 'react';
import { delay, filter, fromEvent, map, tap } from 'rxjs';

import { useSubscriptions } from '@/package';
import {
  useCollectionManagementStore,
  useLabelingBoxStore,
  useTaskFrameStore,
} from '@/store';
import type { FrameData } from '@/type';
import { MESSAGE_FROM, labelingBoxSharedUtil } from '@/worker';
import labelingBoxSharedWorkerUrl from '@/worker/workerScript/labelingBoxShared/labelingBoxSharedWorker?sharedworker&url';

import type {
  __CES2026__CameraConfig,
  __CES2026__CameraNormalizedConfig,
  __CES2026__LidarConfig,
  __CES2026__SystemCalibration,
} from '../type';

const POPUP_READY_DELAY = 100;
const TEXT_DECODER = new TextDecoder();

const useImageWindow = () => {
  const { readFile } = useCollectionManagementStore();
  const {
    cameraCalibrationConfigMap,
    currentFrame,
    selectedTask,
    setCameraCalibrationConfigMap,
  } = useTaskFrameStore();
  const { selectedBoxIdMap, selectBoxList, focusBox } = useLabelingBoxStore();

  const selectedLabelingBoxIdList = useMemo(
    () =>
      Object.entries(selectedBoxIdMap)
        .filter(([_, v]) => v)
        .map(r => r[0]),
    [selectedBoxIdMap],
  );

  const imageViewerWindowRef = useRef<Window | null>(null);
  const sharedWorkerPortRef = useRef<Nullish<MessagePort>>(null);
  const selectedImageIndexRef = useRef<Nullish<number>>(null);

  const closeImageWindow = () => {
    if (imageViewerWindowRef.current && !imageViewerWindowRef.current.closed) {
      imageViewerWindowRef.current.close();
    }
  };

  const openImagePopupInNewWindow = (selectedImageIndex?: number) => {
    if (!selectedTask || !currentFrame) return;

    if (imageViewerWindowRef.current && !imageViewerWindowRef.current.closed) {
      imageViewerWindowRef.current.focus();
      return;
    }

    const newWindow = window.open(
      `/cam/${selectedTask.projectID}/${selectedTask.id}/${currentFrame.frameSeq}`,
      '_blank',
      'width=640,height=480,left=16,top=16',
    );
    if (!newWindow) {
      console.error('팝업이 차단되었습니다.');
      return;
    }

    selectedImageIndexRef.current = selectedImageIndex;
    imageViewerWindowRef.current = newWindow;

    newWindow.addEventListener('beforeunload', () => {
      imageViewerWindowRef.current = null;
    });
  };

  const postMessageToImagePopup = () => {
    const port = sharedWorkerPortRef.current;

    if (!imageViewerWindowRef.current || !currentFrame || !port) return;

    port.postMessage({
      action: 'set',
      from: MESSAGE_FROM.MAIN,
      params: ['cameraCalibrationConfigMap', cameraCalibrationConfigMap],
    });

    port.postMessage({
      action: 'set',
      from: MESSAGE_FROM.MAIN,
      params: ['currentFrame', currentFrame],
    });

    port.postMessage({
      action: 'set',
      from: MESSAGE_FROM.MAIN,
      params: ['selectedLabelingBoxIdList', selectedLabelingBoxIdList],
    });

    port.postMessage({
      action: 'set',
      from: MESSAGE_FROM.MAIN,
      params: ['selectedTask', selectedTask],
    });

    port.postMessage({
      action: 'set',
      from: MESSAGE_FROM.MAIN,
      params: ['selectedImageIndex', selectedImageIndexRef.current],
    });
  };

  useSubscriptions(
    () => [
      fromEvent<MessageEvent>(window, 'message')
        .pipe(
          map(ev => ev.data as { data: any; type: string }),
          filter(data => data?.type === 'camPopupPageReady'),
          delay(POPUP_READY_DELAY),
          tap(() => postMessageToImagePopup()),
        )
        .subscribe(),
    ],
    [
      cameraCalibrationConfigMap,
      currentFrame,
      selectedLabelingBoxIdList,
      selectedTask,
    ],
  );

  useEffect(() => {
    if (!selectedTask || !selectedTask.camCalFileName) {
      setCameraCalibrationConfigMap({});
      return;
    }

    const { basePath, camCalFileName, orgPath } = selectedTask;
    const camCalFilePath = `${basePath}/${orgPath.replace(/(\.pcd)(?=\/$)/, '.img')}${camCalFileName}`;

    readFile({ key: camCalFilePath })
      .then(res => res.body)
      .then(buffer => (buffer ? TEXT_DECODER.decode(buffer) : ''))
      .then(str => JSON.parse(str) as __CES2026__SystemCalibration)
      .then(calibration => {
        const [rawCameraList, lidarList] = [
          calibration.cameras,
          calibration.lidars,
        ];

        const newCameraCalibrationConfigMap = {} as {
          [
            imageFileName: __CES2026__CameraNormalizedConfig['name']
          ]: __CES2026__CameraNormalizedConfig;
        };

        rawCameraList.forEach(camera => {
          const name = camera['0_name'];
          const model = camera['1_model'];
          const intrinsic = camera['3_intrinsic'];
          const intrinsicProjErr = camera['3_intrinsic_projErr'];
          const extrinsic = camera['4_extrinsic'];

          newCameraCalibrationConfigMap[name] = {
            name,
            model,
            intrinsic,
            intrinsicProjErr,
            extrinsic,
            lidarList: lidarList.map(v => v.calibration),
          };
        });

        setCameraCalibrationConfigMap(newCameraCalibrationConfigMap);
      })
      .catch(err => {
        console.group('[useImageWindow] download camCalFilePath Error');
        console.warn(err);
        console.groupEnd();
        setCameraCalibrationConfigMap({});
      });
  }, [selectedTask]);

  useEffect(() => {
    postMessageToImagePopup();
  }, [
    cameraCalibrationConfigMap,
    currentFrame,
    selectedLabelingBoxIdList,
    selectedTask,
  ]);

  useEffect(() => {
    const sharedWorker = new SharedWorker(labelingBoxSharedWorkerUrl, {
      credentials: 'same-origin',
      type: 'module',
    });
    const channel = new MessageChannel();

    const port = sharedWorker.port;

    const onBeforeUnload = () => {
      port.postMessage({ action: 'clear', params: [] });
      port.close();
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    port.addEventListener('message', e => {
      const { from, payload } = e.data;

      if (
        !labelingBoxSharedUtil.isValidEventPostMessage(e.data) ||
        from === MESSAGE_FROM.MAIN
      )
        return;

      switch (e.data.type) {
        case 'changeSelectedLabelingBoxIdList':
          if (Array.isArray(payload)) {
            selectBoxList(payload as FrameData['workData'][0]['id'][]);
          }
          break;
        case 'focusBox':
          if (payload != null) {
            focusBox(payload);
          }
          break;
      }
    });

    port.start();

    port.postMessage({ type: 'initPort' }, [channel.port2]);

    sharedWorkerPortRef.current = port;

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      port.postMessage({ action: 'clear', params: [] });
      port.close();
      sharedWorkerPortRef.current = null;
    };
  }, []);

  return { closeImageWindow, openImagePopupInNewWindow };
};

export default useImageWindow;
